import { Inject, Injectable } from '@angular/core';
import { NotificationsService, NotificationType } from 'angular2-notifications';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from '../../config/constants.module';

/**
 * @class MessageService
 * @version 0.0.1
 * @since September 2017
 * @author Tuan La
 * @description The MessageService is a wrapper class for the
 * angular2-notifications library. It allows the user to create a notification
 * message with following params :
 *    1.  a given i18n translation key, which points to a JSON object with
 *        two attributes "title" as must field and "body" as optional field
 *    2.  Optionally the duration of the message to be shown befor disappearing
 *
 *  For more information about customization go to toastNotification Doc of
 *  https://github.com/flauc/angular2-notifications
 */
@Injectable({
  providedIn: 'root',
})
export class MessageService {
  constructor(
    @Inject(NotificationsService) private _notiService: NotificationsService,
    @Inject(TranslateService) private _translate: TranslateService,
    @Inject(CONSTANTS) private _CONST: any
  ) {}

  public success(key: string, duration?: number) {
    this._translate.get(key).subscribe(message => {
      const title = message.TITLE;
      const body = message.BODY ? message.BODY : null;

      this.createMessage(title, NotificationType.Success, body, duration);
    });
  }

  public error(key: string, duration?: number) {
    this._translate.get(key).subscribe(message => {
      const title = message.TITLE;
      const body = message.BODY ? message.BODY : null;

      this.createMessage(title, NotificationType.Error, body, duration);
    });
  }

  public alert(key: string, duration?: number) {
    this._translate.get(key).subscribe(message => {
      const title = message.TITLE;
      const body = message.BODY ? message.BODY : null;

      this.createMessage(title, NotificationType.Alert, body, duration);
    });
  }

  public warning(key: string, duration?: number) {
    this._translate.get(key).subscribe(message => {
      const title = message.TITLE;
      const body = message.BODY ? message.BODY : null;

      this.createMessage(title, NotificationType.Warn, body, duration);
    });
  }

  public info(key: string, duration?: number) {
    this._translate.get(key).subscribe(message => {
      const title = message.TITLE;
      const body = message.BODY ? message.BODY : null;

      this.createMessage(title, NotificationType.Info, body, duration);
    });
  }

  public bare(key: string, duration?: number) {
    this._translate.get(key).subscribe(message => {
      const title = message.TITLE;
      const body = message.BODY ? message.BODY : null;

      this.createMessage(title, NotificationType.Bare, body, duration);
    });
  }

  /**
   * @function createCustomMessage
   * @param {string} title
   * @param {string} type
   * @param {string} body
   * @param {number} duration
   * @description Allows the creation of a custom message
   */
  public createCustomMessage(title: string, body: string, type: NotificationType, options?: any) {
    this._notiService.create(
      title,
      body ? body : '',
      type ? type : NotificationType.Info,
      options ? options : null
    );
  }

  /* Private functions
  ======================== */
  /**
   * @function createMessage
   * @param {string} title
   *  The title of the message (must field)
   * @param {string} type
   *  The generic type of the message (must field)
   * @param {string} body
   *  The body of the message (optional field)
   * @param {number} duration
   *  The duration of the message (optional field)
   * @description
   *  This function requests a given message type and create
   *  accordingly a message with the correct CSS style
   */
  private createMessage(title: string, type: NotificationType, body?: string, duration?: number) {
    this._notiService.create(title, body ? body : null, type, {
      timeOut: duration ? duration : this._CONST.NOTIFICATION_TIMEOUT.SECONDS_10,
    });
  }
}
